import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, MessageSquare } from 'react-feather';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  metadata?: any;
}

interface FeedbackButtonsProps {
  responseId: string;
  conversationId: string;
  messageId: string;
  originalQuestion: string;
  originalAnswer: string;
  originalSources: any[];
  conversationHistory: Message[];
}

const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  responseId,
  conversationId,
  messageId,
  originalQuestion,
  originalAnswer,
  originalSources,
  conversationHistory
}) => {
  const [showDetailedForm, setShowDetailedForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quickFeedbackSubmitted, setQuickFeedbackSubmitted] = useState<'positive' | 'negative' | null>(null);
  const [detailedFeedbackSubmitted, setDetailedFeedbackSubmitted] = useState(false);

  const formatConversationHistory = () => {
    return conversationHistory.map(msg => ({
      type: msg.type,
      content: msg.content,
      timestamp: msg.timestamp.toISOString(),
      messageId: msg.id,
      ...(msg.metadata && { metadata: msg.metadata })
    }));
  };

  const handleQuickFeedback = async (rating: 'positive' | 'negative') => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Submit simple feedback via activity logs API
      const response = await fetch('/api/activity-logs/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationId,
          entryId: messageId,
          rating,
          feedbackType: 'quick',
          category: 'response_quality',
          conversationHistory: formatConversationHistory()
        })
      });

      if (response.ok) {
        setQuickFeedbackSubmitted(rating);
      } else {
        console.error('Failed to submit feedback:', await response.text());
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDetailedFeedback = () => {
    setShowDetailedForm(true);
  };

  if (detailedFeedbackSubmitted) {
    return (
      <div className="feedback-buttons">
        <div className="feedback-success">
          <span className="text-xs text-eddy-green">
            ✓ Thank you for your feedback!
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="feedback-buttons">
        <button
          onClick={() => handleQuickFeedback('positive')}
          disabled={isSubmitting}
          className={`feedback-btn feedback-btn-positive ${quickFeedbackSubmitted === 'positive' ? 'bg-eddy-green/20 border-eddy-green text-eddy-green' : ''}`}
          title="This response was helpful"
        >
          <ThumbsUp size={14} />
        </button>
        <button
          onClick={() => handleQuickFeedback('negative')}
          disabled={isSubmitting}
          className={`feedback-btn feedback-btn-negative ${quickFeedbackSubmitted === 'negative' ? 'bg-eddy-orange/20 border-eddy-orange text-eddy-orange' : ''}`}
          title="This response was not helpful"
        >
          <ThumbsDown size={14} />
        </button>
        <button
          onClick={handleDetailedFeedback}
          disabled={isSubmitting}
          className="feedback-btn feedback-btn-detailed"
          title="Provide detailed feedback"
        >
          <MessageSquare size={14} />
        </button>
        {quickFeedbackSubmitted && (
          <span className="text-xs text-eddy-green ml-2">
            ✓ Thanks!
          </span>
        )}
      </div>

      {showDetailedForm && (
        <DetailedFeedbackForm
          responseId={responseId}
          conversationId={conversationId}
          messageId={messageId}
          originalQuestion={originalQuestion}
          originalAnswer={originalAnswer}
          originalSources={originalSources}
          conversationHistory={conversationHistory}
          onClose={() => setShowDetailedForm(false)}
          onSubmitted={() => {
            setShowDetailedForm(false);
            setDetailedFeedbackSubmitted(true);
          }}
        />
      )}
    </>
  );
};

interface DetailedFeedbackFormProps {
  responseId: string;
  conversationId: string;
  messageId: string;
  originalQuestion: string;
  originalAnswer: string;
  originalSources: any[];
  conversationHistory: Message[];
  onClose: () => void;
  onSubmitted: () => void;
}

const DetailedFeedbackForm: React.FC<DetailedFeedbackFormProps> = ({
  responseId,
  conversationId,
  messageId,
  originalQuestion,
  originalAnswer,
  originalSources,
  conversationHistory,
  onClose,
  onSubmitted
}) => {
  const [feedbackType, setFeedbackType] = useState<'general' | 'incorrect' | 'incomplete' | 'outdated' | 'unclear' | 'helpful' | 'unhelpful'>('general');
  const [correctedAnswer, setCorrectedAnswer] = useState('');
  const [explanation, setExplanation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-scroll form into view when mounted
  React.useEffect(() => {
    // Give the DOM a moment to render, then scroll
    setTimeout(() => {
      const form = document.querySelector('.detailed-feedback-form');
      if (form) {
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }, []);

  const formatConversationHistory = () => {
    return conversationHistory.map(msg => ({
      type: msg.type,
      content: msg.content,
      timestamp: msg.timestamp.toISOString(),
      messageId: msg.id,
      ...(msg.metadata && { metadata: msg.metadata })
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Submit detailed feedback to correction API (for teaching system)
      const correctionResponse = await fetch('/api/chat/correction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          responseId,
          conversationId,
          originalQuestion,
          originalAnswer,
          originalSources,
          feedbackType,
          correctedAnswer: correctedAnswer.trim() || undefined,
          explanation: explanation.trim() || undefined,
          conversationHistory: formatConversationHistory(),
          contributor: {
            id: 'anonymous',
            name: 'Anonymous User',
            email: 'anonymous@example.com',
            role: 'user'
          }
        })
      });

      // Determine appropriate rating based on feedback type
      const getRatingFromFeedbackType = (type: string): 'positive' | 'negative' => {
        const positiveTypes = ['helpful', 'general'];
        const negativeTypes = ['incorrect', 'incomplete', 'outdated', 'unclear', 'unhelpful'];
        
        if (positiveTypes.includes(type)) return 'positive';
        if (negativeTypes.includes(type)) return 'negative';
        return 'negative'; // Default to negative for unknown types
      };

      // Also submit to activity logs for admin visibility
      const activityLogResponse = await fetch('/api/activity-logs/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationId,
          entryId: messageId,
          rating: getRatingFromFeedbackType(feedbackType),
          comment: `${feedbackType}: ${explanation.trim() || correctedAnswer.trim() || 'Detailed feedback provided'}`,
          feedbackType: 'detailed',
          category: feedbackType,
          conversationHistory: formatConversationHistory()
        })
      });

      if (correctionResponse.ok && activityLogResponse.ok) {
        onSubmitted();
      } else {
        if (!correctionResponse.ok) {
          console.error('Failed to submit detailed feedback to correction API:', await correctionResponse.text());
        }
        if (!activityLogResponse.ok) {
          console.error('Failed to submit detailed feedback to activity logs:', await activityLogResponse.text());
        }
        alert('Failed to submit feedback. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting detailed feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="detailed-feedback-form">
      <div className="feedback-form-header">
        <h4>Provide Detailed Feedback</h4>
        <button onClick={onClose} className="close-btn">×</button>
      </div>
      
      <form onSubmit={handleSubmit} className="feedback-form">
        <div className="form-group">
          <label htmlFor="feedbackType">Feedback Type:</label>
          <select
            id="feedbackType"
            value={feedbackType}
            onChange={(e) => setFeedbackType(e.target.value as any)}
            className="form-select"
          >
            <option value="general">General</option>
            <option value="incorrect">Incorrect information</option>
            <option value="incomplete">Incomplete answer</option>
            <option value="outdated">Outdated information</option>
            <option value="unclear">Unclear or confusing</option>
            <option value="helpful">Helpful response</option>
            <option value="unhelpful">Unhelpful response</option>
          </select>
        </div>

        {(['incorrect', 'incomplete', 'outdated', 'unclear'].includes(feedbackType)) && (
          <div className="form-group">
            <label htmlFor="correctedAnswer">Corrected Answer (optional):</label>
            <textarea
              id="correctedAnswer"
              value={correctedAnswer}
              onChange={(e) => setCorrectedAnswer(e.target.value)}
              placeholder="Provide the correct information..."
              className="form-textarea"
              rows={4}
            />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="explanation">Additional Comments (optional):</label>
          <textarea
            id="explanation"
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            placeholder="Any additional context or explanation..."
            className="form-textarea"
            rows={3}
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FeedbackButtons;